import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, MagnifyingGlass, Export, FunnelSimple, X, UserCircle } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../lib/auth';
import { getCustomers, getConfigOptions, exportCustomers } from '../lib/api';

const LIFECYCLE_COLORS = {
  'Lead': 'bg-blue-100 text-blue-800',
  'Active Prospect': 'bg-yellow-100 text-yellow-800',
  'Sampled': 'bg-purple-100 text-purple-800',
  'First Order Done': 'bg-green-100 text-green-800',
  'Recurring': 'bg-emerald-100 text-emerald-800',
  'Dormant': 'bg-gray-100 text-gray-600',
  'Lost': 'bg-red-100 text-red-800',
};

const CREDIT_COLORS = {
  'Not Evaluated': 'bg-gray-100 text-gray-600',
  'Approved': 'bg-green-100 text-green-800',
  'On Hold': 'bg-yellow-100 text-yellow-800',
  'Blocked': 'bg-red-100 text-red-800',
};

export default function CustomerList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [filters, setFilters] = useState({
    customer_type: searchParams.get('customer_type') || '',
    channel: searchParams.get('channel') || '',
    lifecycle_stage: searchParams.get('lifecycle_stage') || '',
    credit_status: searchParams.get('credit_status') || '',
    country: searchParams.get('country') || '',
  });
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [showFilters, setShowFilters] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [configOptions, setConfigOptions] = useState({});

  const limit = 25;

  useEffect(() => {
    Promise.all([
      getConfigOptions('customer_types'),
      getConfigOptions('channels'),
      getConfigOptions('lifecycle_stages'),
      getConfigOptions('credit_statuses'),
    ]).then(([types, channels, stages, statuses]) => {
      setConfigOptions({
        customer_types: types.data.values.filter(v => v.is_active),
        channels: channels.data.values.filter(v => v.is_active),
        lifecycle_stages: stages.data.values.filter(v => v.is_active),
        credit_statuses: statuses.data.values.filter(v => v.is_active),
      });
    }).catch(() => {});
  }, []);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit, search: search || undefined };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await getCustomers(params);
      setCustomers(res.data.customers);
      setTotal(res.data.total_count);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [page, search, filters]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  useEffect(() => {
    const params = {};
    if (search) params.search = search;
    if (page > 1) params.page = page;
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    setSearchParams(params, { replace: true });
  }, [search, page, filters, setSearchParams]);

  const handleExport = async (format) => {
    try {
      const res = await exportCustomers({ format });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customers_export.${format === 'csv' ? 'csv' : 'xlsx'}`;
      a.click();
      window.URL.revokeObjectURL(url);
      setExportOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  const clearFilters = () => {
    setFilters({ customer_type: '', channel: '', lifecycle_stage: '', credit_status: '', country: '' });
    setPage(1);
  };
  const hasFilters = Object.values(filters).some(v => v);
  const totalPages = Math.ceil(total / limit);
  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  return (
    <div className="space-y-6" data-testid="customer-list">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          <p className="text-sm text-gray-500 mt-1">{total} customer{total !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          {can('export_customers') && (
            <Button variant="outline" onClick={() => setExportOpen(true)} data-testid="export-customers-btn">
              <Export size={18} className="mr-2" />Export
            </Button>
          )}
          {can('create_customers') && (
            <Button asChild data-testid="new-customer-btn">
              <Link to="/customers/new"><Plus size={18} className="mr-2" />New Customer</Link>
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search company, contact, email, tag..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="pl-10"
            data-testid="customer-search"
          />
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters">
          <FunnelSimple size={16} className="mr-1" />Filters
          {hasFilters && <span className="ml-1 w-2 h-2 rounded-full bg-blue-500 inline-block" />}
        </Button>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}><X size={14} className="mr-1" />Clear</Button>
        )}
      </div>

      {showFilters && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4 bg-gray-50 rounded-lg border" data-testid="filter-panel">
          <Select value={filters.customer_type} onValueChange={v => { setFilters(f => ({...f, customer_type: v === 'all' ? '' : v})); setPage(1); }}>
            <SelectTrigger><SelectValue placeholder="Customer Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {(configOptions.customer_types || []).map(t => <SelectItem key={t.key} value={t.label}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.channel} onValueChange={v => { setFilters(f => ({...f, channel: v === 'all' ? '' : v})); setPage(1); }}>
            <SelectTrigger><SelectValue placeholder="Channel" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Channels</SelectItem>
              {(configOptions.channels || []).map(t => <SelectItem key={t.key} value={t.label}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.lifecycle_stage} onValueChange={v => { setFilters(f => ({...f, lifecycle_stage: v === 'all' ? '' : v})); setPage(1); }}>
            <SelectTrigger><SelectValue placeholder="Lifecycle" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stages</SelectItem>
              {(configOptions.lifecycle_stages || []).map(t => <SelectItem key={t.key} value={t.label}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.credit_status} onValueChange={v => { setFilters(f => ({...f, credit_status: v === 'all' ? '' : v})); setPage(1); }}>
            <SelectTrigger><SelectValue placeholder="Credit Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {(configOptions.credit_statuses || []).map(t => <SelectItem key={t.key} value={t.label}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Country" value={filters.country} onChange={e => { setFilters(f => ({...f, country: e.target.value})); setPage(1); }} />
        </div>
      )}

      <div className="border rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Company</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Channel</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Country</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Lifecycle</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Credit</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Contact</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-12 text-gray-400">Loading...</td></tr>
            ) : customers.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-gray-400">
                <UserCircle size={40} className="mx-auto mb-2 opacity-30" />
                No customers found
              </td></tr>
            ) : customers.map(c => {
              const primary = (c.contacts || []).find(ct => ct.is_primary) || (c.contacts || [])[0];
              return (
                <tr key={c.id} className="border-b last:border-0 hover:bg-gray-50 cursor-pointer" onClick={() => window.location.href = `/customers/${c.id}`} data-testid={`customer-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{c.company_name}</div>
                    {c.display_name !== c.company_name && <div className="text-xs text-gray-500">{c.display_name}</div>}
                    {(c.tags || []).length > 0 && (
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {c.tags.slice(0, 3).map(t => <Badge key={t} variant="secondary" className="text-xs px-1.5 py-0">{t}</Badge>)}
                        {c.tags.length > 3 && <span className="text-xs text-gray-400">+{c.tags.length - 3}</span>}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{(c.customer_types || []).join(', ') || '-'}</td>
                  <td className="px-4 py-3 text-gray-600">{(c.channels || []).join(', ') || '-'}</td>
                  <td className="px-4 py-3 text-gray-600">{c.country || '-'}</td>
                  <td className="px-4 py-3">
                    <Badge className={`text-xs ${LIFECYCLE_COLORS[c.lifecycle_stage] || 'bg-gray-100'}`}>
                      {c.lifecycle_stage || '-'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={`text-xs ${CREDIT_COLORS[c.credit?.credit_status] || 'bg-gray-100'}`}>
                      {c.credit?.credit_status || '-'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {primary ? <div><div className="text-xs">{primary.name}</div><div className="text-xs text-gray-400">{primary.email}</div></div> : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between" data-testid="pagination">
          <span className="text-sm text-gray-500">Showing {startItem}-{endItem} of {total}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Export Customers</DialogTitle></DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button className="flex-1" onClick={() => handleExport('xlsx')} data-testid="export-xlsx">Download Excel</Button>
            <Button variant="outline" className="flex-1" onClick={() => handleExport('csv')} data-testid="export-csv">Download CSV</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
